import React from "react";
import { cn } from "@/lib/utils";

type ShimmerOverlayProps = {
  isLoading: boolean;
  className?: string;
  overlayClassName?: string;
  children: React.ReactNode;
};

/**
 * ShimmerOverlay
 * - While isLoading is true: shows a soft white layer with a left-to-right shimmer.
 * - When loading completes: overlay fades out; content fades in with a slight rise.
 * Usage: wrap any card/section that should show the loading effect.
 */
export function ShimmerOverlay({
  isLoading,
  className,
  overlayClassName,
  children,
}: ShimmerOverlayProps) {
  return (
    <div className={cn("relative", className)}>
      {/* Content with fade-in + slight rise */}
      <div
        className={cn(
          "transition-all duration-500 ease-out",
          isLoading ? "opacity-0 translate-y-1 pointer-events-none" : "opacity-100 translate-y-0"
        )}
      >
        {children}
      </div>
      {/* Overlay with shimmer */}
      <div
        aria-hidden
        className={cn(
          "absolute inset-0 rounded-[inherit] overflow-hidden",
          "bg-white/10 backdrop-blur-[1px]",
          "transition-opacity duration-500 ease-out",
          isLoading ? "opacity-100" : "opacity-0 pointer-events-none",
          overlayClassName
        )}
      >
        <div className="absolute inset-0">
          <div
            className={cn(
              "absolute top-0 left-[-50%] h-full w-[200%]",
              "bg-gradient-to-r from-transparent via-white/30 to-transparent",
              "animate-shimmer"
            )}
          />
        </div>
      </div>
    </div>
  );
}

type ShimmerBlockProps = React.HTMLAttributes<HTMLDivElement> & {
  rounded?: string;
};

/**
 * ShimmerBlock
 * A simple rectangular skeleton block with shimmer. Useful for layout placeholders.
 */
export function ShimmerBlock({ className, rounded, ...props }: ShimmerBlockProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden bg-white/5",
        rounded ?? "rounded-xl",
        className
      )}
      {...props}
    >
      <div className="absolute inset-0 bg-white/5" />
      <div className="absolute top-0 left-[-50%] h-full w-[200%] bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
    </div>
  );
}

export default ShimmerOverlay;
